import {FC} from "react";
import {useIsMobile} from "../../../../hooks/useIsMobile.ts";
import {App, Badge, Button, Flex, Layout, Menu, MenuProps, Popover, theme, Typography} from "antd";
import {LogoutOutlined, MenuOutlined, PlusOutlined, SettingOutlined, TeamOutlined} from "@ant-design/icons";
import {Link, useLocation, useNavigate} from "react-router";
import {UserAvatar} from "../../../UserAvatar/UserAvatar.tsx";
import styles from './Header.module.css';
import {authDelete} from "../../../../api/auth/authDelete.ts";
import {useEvents} from "../../../../queries/useEvents.ts";
import {useOwnProfile} from "../../../../stores/OwnProfileStore.ts";

export const Header: FC = () => {
    const isMobile = useIsMobile();
    const { token } = theme.useToken();

    const { profile, setProfile } = useOwnProfile();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { message } = App.useApp();

    const { data: events } = useEvents('incoming');
    const { data: eventsDrafts } = useEvents('drafts');

    const handleLogout = () => {
        authDelete().then(() => {
            setProfile(null);
            navigate('/auth');
        }).catch((e) => {
            void message.error(`Произошла ошибка (${JSON.stringify(e)})`);
        });
    }

    const menuItems: MenuProps['items'] = [
        // требуется мое согласование или мне вернули ивент как создающему
        ...(profile?.role !== 'fir_reader' ? [{
            key: '/',
            label: (
                <Link to="/">
                    <Flex align="center" gap={8}>
                        Входящие
                        {Boolean(events?.length) && <Badge size="small" count={events!.length} />}
                    </Flex>
                </Link>
            )
        }] : []),
        // (на согласовании) я согласовал или я создал - ушел дальше по цепочке
        {
            key: '/processing',
            label: (
                <Link to="/processing">В обработке</Link>
            )
        },
        // согласован всеми - утвержден
        {
            key: '/complete',
            label: (
                <Link to="/complete">Утвержденные</Link>
            )
        },
        // утвержден и прошел более мес назад или отменен
        {
            key: '/archive',
            label: (
                <Link to="/archive">Архив</Link>
            )
        },
        ...(profile?.role !== 'fir_reader' ? [{
            key: '/drafts',
            label: (
                <Link to="/drafts">
                    <Flex align="center" gap={8}>
                        Черновики
                        {Boolean(eventsDrafts?.length) && <Badge size="small" count={eventsDrafts!.length} />}
                    </Flex>
                </Link>
            )
        }] : []),
        {
            key: '/calendar',
            label: (
                <Link to="/calendar">Календарь</Link>
            )
        },
        ...(profile?.role === 'staff' || profile?.admin ? [{
            key: '/admin',
            label: (
                <Link to="/admin">Админка</Link>
            )
        }] : [])
    ];

    const mobileMenuItems: MenuProps['items'] = [
        {
            key: 'mob-create',
            label: (
                <Link to="/events/create">
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                    >Создать</Button>
                </Link>
            )
        },
        ...menuItems,
        ...(profile ? [
            {
                type: 'divider' as const
            },
            {
                key: 'mob-settings',
                label: (
                    <Link to="/settings">
                        <Button
                            type="text"
                            icon={<SettingOutlined />}
                        >Настройки</Button>
                    </Link>
                ),
            },
            {
                key: 'mob-logout',
                label: (
                    <Button
                        type="primary"
                        danger
                        block
                        icon={<LogoutOutlined />}
                        onClick={handleLogout}
                    >Выйти</Button>
                )
            }
        ] : [])
    ]

    return (
        <Layout.Header className={styles.header}>
            <Flex align="center" gap={16} className={styles.leftContainer}>
                <Flex align="center" gap={2} className={styles.iconTitleContainer}>
                    <TeamOutlined className={styles.icon} />
                    <Link to="/" className={styles.title}>VATRUS Events</Link>
                    <Typography.Text className={styles.version}>v{__APP_VERSION__}</Typography.Text>
                </Flex>
                {!isMobile && (
                    <>
                        <Link to="/events/create">
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                            >Создать</Button>
                        </Link>
                        <Menu
                            theme="dark"
                            mode="horizontal"
                            selectedKeys={[pathname]}
                            items={menuItems}
                            className={styles.menu}
                        />
                    </>
                )}
            </Flex>
            {!isMobile && profile && (
                <Popover
                    trigger="click"
                    placement="bottom"
                    classNames={{ body: styles.profilePopoverBody }}
                    content={(
                        <Flex vertical gap={6}>
                            <Link to="/settings">
                                <Button
                                    type="text"
                                    icon={<SettingOutlined />}
                                >Настройки</Button>
                            </Link>
                            <Button
                                type="primary"
                                danger
                                block
                                icon={<LogoutOutlined />}
                                onClick={handleLogout}
                            >Выйти</Button>
                        </Flex>
                    )}
                >
                    <Button type="text">
                        <Flex align="center" gap={8}>
                            <UserAvatar user={profile} />
                            <Typography.Text className={styles.profileName}>
                                {profile.first_name} {profile.last_name}
                            </Typography.Text>
                        </Flex>
                    </Button>
                </Popover>
            )}
            {isMobile && (
                <Popover
                    trigger="click"
                    placement="bottom"
                    styles={{ body: { padding: 0, overflow: 'hidden' } }}
                    content={(
                        <Menu
                            mode="inline"
                            disabledOverflow
                            items={mobileMenuItems}
                            selectedKeys={[pathname]}
                        />
                    )}
                >
                    <Button type="text" icon={<MenuOutlined style={{ color: token.colorTextLightSolid }} />} />
                </Popover>
            )}
        </Layout.Header>
    )
}